import React, { createContext, useContext, useEffect, useMemo, useState } from 'react';

type ThemeMode = 'light' | 'dark';
type ColorTheme = 'natural' | 'slate' | 'nord' | 'midnight' | 'warm';

export interface ThemePreset {
  id: ColorTheme;
  name: string;
  description: string;
  colors: {
    primary: string;
    secondary: string;
    accent: string;
    background: string;
    text: string;
  };
}

const THEME_PRESETS: ThemePreset[] = [
  {
    id: 'natural',
    name: 'Natural',
    description: 'Clean neutral grays',
    colors: {
      primary: '#6366f1',
      secondary: '#4f46e5',
      accent: '#818cf8',
      background: '#fafafa',
      text: '#171717',
    },
  },
  {
    id: 'slate',
    name: 'Slate',
    description: 'Modern blue-gray tones',
    colors: {
      primary: '#6366f1',
      secondary: '#4f46e5',
      accent: '#818cf8',
      background: '#f8fafc',
      text: '#0f172a',
    },
  },
  {
    id: 'nord',
    name: 'Nord',
    description: 'Nordic soft palette',
    colors: {
      primary: '#5e81ac',
      secondary: '#5e81ac',
      accent: '#81a1c1',
      background: '#eceff4',
      text: '#2e3440',
    },
  },
  {
    id: 'midnight',
    name: 'Midnight',
    description: 'Deep blue theme',
    colors: {
      primary: '#6366f1',
      secondary: '#4f46e5',
      accent: '#818cf8',
      background: '#f0f4f8',
      text: '#102a43',
    },
  },
  {
    id: 'warm',
    name: 'Warm',
    description: 'Coffee and earth tones',
    colors: {
      primary: '#6366f1',
      secondary: '#4f46e5',
      accent: '#818cf8',
      background: '#faf8f5',
      text: '#2a1f18',
    },
  },
];

interface UISettingsState {
  isSidebarCollapsed: boolean;
  themeMode: ThemeMode;
  colorTheme: ColorTheme;
}

interface UISettingsContextValue extends UISettingsState {
  themePresets: ThemePreset[];
  toggleSidebar: () => void;
  setSidebarCollapsed: (collapsed: boolean) => void;
  toggleThemeMode: () => void;
  setThemeMode: (mode: ThemeMode) => void;
  setColorTheme: (theme: ColorTheme) => void;
}

const STORAGE_KEYS = {
  sidebarCollapsed: 'ui.sidebarCollapsed',
  themeMode: 'ui.themeMode',
  colorTheme: 'ui.colorTheme',
} as const;

const DEFAULT_STATE: UISettingsState = {
  isSidebarCollapsed: false,
  themeMode: 'light',
  colorTheme: 'natural',
};

const isValidTheme = (value: string | null): value is ColorTheme =>
  !!value && THEME_PRESETS.some((theme) => theme.id === value);

const getInitialState = (): UISettingsState => {
  if (typeof window === 'undefined') {
    return DEFAULT_STATE;
  }

  const sidebarCollapsed = localStorage.getItem(STORAGE_KEYS.sidebarCollapsed);
  const themeMode = localStorage.getItem(STORAGE_KEYS.themeMode);
  const colorTheme = localStorage.getItem(STORAGE_KEYS.colorTheme);

  return {
    isSidebarCollapsed: sidebarCollapsed === 'true',
    themeMode: themeMode === 'dark' ? 'dark' : 'light',
    colorTheme: isValidTheme(colorTheme) ? colorTheme : 'natural',
  };
};

const UISettingsContext = createContext<UISettingsContextValue | undefined>(
  undefined
);

export const UISettingsProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const [state, setState] = useState<UISettingsState>(() => getInitialState());

  useEffect(() => {
    if (typeof window === 'undefined') {
      return;
    }
    localStorage.setItem(
      STORAGE_KEYS.sidebarCollapsed,
      state.isSidebarCollapsed ? 'true' : 'false'
    );
    localStorage.setItem(STORAGE_KEYS.themeMode, state.themeMode);
    localStorage.setItem(STORAGE_KEYS.colorTheme, state.colorTheme);
  }, [state.colorTheme, state.isSidebarCollapsed, state.themeMode]);

  useEffect(() => {
    const root = document.documentElement;
    root.classList.toggle('dark', state.themeMode === 'dark');
    root.setAttribute('data-theme', state.colorTheme);
    root.style.setProperty(
      '--sidebar-current-width',
      state.isSidebarCollapsed
        ? 'var(--sidebar-collapsed)'
        : 'var(--sidebar-width)'
    );
  }, [state.colorTheme, state.isSidebarCollapsed, state.themeMode]);

  const value = useMemo<UISettingsContextValue>(
    () => ({
      ...state,
      themePresets: THEME_PRESETS,
      toggleSidebar: () =>
        setState((prev) => ({
          ...prev,
          isSidebarCollapsed: !prev.isSidebarCollapsed,
        })),
      setSidebarCollapsed: (collapsed) =>
        setState((prev) => ({ ...prev, isSidebarCollapsed: collapsed })),
      toggleThemeMode: () =>
        setState((prev) => ({
          ...prev,
          themeMode: prev.themeMode === 'dark' ? 'light' : 'dark',
        })),
      setThemeMode: (mode) =>
        setState((prev) => ({ ...prev, themeMode: mode })),
      setColorTheme: (theme) =>
        setState((prev) => ({ ...prev, colorTheme: theme })),
    }),
    [state]
  );

  return (
    <UISettingsContext.Provider value={value}>
      {children}
    </UISettingsContext.Provider>
  );
};

export const useUISettings = () => {
  const context = useContext(UISettingsContext);
  if (!context) {
    throw new Error('useUISettings must be used within UISettingsProvider');
  }
  return context;
};
